import React, { useCallback, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import OrderRowProductCard from '../OrderRowProductCard/OrderRowProductCard';
import CheckoutSection from './utils/Checkout.Section';
import CheckoutSectionContent from './utils/Checkout.SectionContent';
import { translate } from '../../Services/translation';
import CheckoutOrderSummary from './Checkout.OrderSummary';
import { update } from '../../Actions/Cart.action';
import { useTypedSelector } from '../../Hooks/useTypedSelector';
import styled from 'styled-components';
import { reloadPayment } from '../../Actions/Checkout.action';
import { getColor } from '../../styling/mixins/colors';
import { text } from '../../styling/mixins/typography';
import Discount from './Checkout.Discount';

const Cart = () => {
  const dispatch = useDispatch();

  // Selectors for orderRows, errors, and quantity
  const orderRows = useTypedSelector((state) => state.cart.orderRows);
  const errors = useTypedSelector((state) => state.cart.errors);
  const quantity = useTypedSelector((state) => state.cart.quantity);

  // Handle removing a row and updating state
  const removeRow = useCallback(
    (row) => {
      dispatch(update(row.articleNumber, 0)).then(() => dispatch(reloadPayment()));
    },
    [dispatch]
  );

  function onQuantityChange(
    quantity: number,
    articleNumber: string,
    abortController: AbortController
  ) {
    dispatch(update(articleNumber, quantity, abortController))
      .then(() => dispatch(reloadPayment()))
      .catch((err) => {
        if (err.name !== 'AbortError') {
          console.error('API error:', err);
        }
      });
  }

  // Memoize whether there are orderRows
  const hasOrderRows = useMemo(() => orderRows?.length > 0, [orderRows]);

  // Memoize the orderRows mapping to prevent unnecessary rerenders
  const orderRowCards = useMemo(() => {
    return (
      hasOrderRows &&
      orderRows.map((row) => (
        <OrderRowProductCard
          key={row.rowSystemId + 'checkout-desktop-cart'}
          row={row}
          onRemoveClick={removeRow}
          onQuantityChange={onQuantityChange}
          className='lg:border-none border border-solid border-gray-200'
        />
      ))
    );
  }, [hasOrderRows, orderRows, removeRow]);

  return (
    <StyledCheckoutCart>
      <CheckoutSection
        className="checkout-cart"
        headingClassName='font-satoshi-black'
        headerClassName='lg:!pl-6'
        title={translate('checkout.cart.title') + ' (' + quantity + ')'}
      >
        {errors && errors["general"]?.length > 0 && <p className="stock-error">{errors["general"][0]}</p>}
        <div className='lg:bg-gray-100'>
          <CheckoutSectionContent className=''>{orderRowCards}</CheckoutSectionContent>
          <CheckoutSectionContent className='discount-section'>
            <Discount />
          </CheckoutSectionContent>
          <CheckoutSectionContent className='order-summary-section'>
            <CheckoutOrderSummary />
          </CheckoutSectionContent>
        </div>
      </CheckoutSection>
    </StyledCheckoutCart>
  );
};

export default Cart;

const StyledCheckoutCart = styled.div`
  .stock-error {
    color: ${getColor('alert')};
    ${text('small')};
  }

  .discount-section {
    padding: 2.4rem 0;
    background-color: white;
  }

  @media(min-width: 1024px){
    .checkout-cart{
      padding: 1.4rem 0 !important;
    } 

    .discount-section {
      padding: 2.4rem 0;
      margin-top: 0.4rem;
    }

    .order-summary-section {
      margin-top: 0.4rem;
    }
  }
`;